import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useApp } from '../../context/AppContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiPlus, FiMinus, FiShoppingCart } = FiIcons;

function Menu({ selectedPrison, visitingDetails }) {
  const { inventory, addToCart, cart } = useApp();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [addedItems, setAddedItems] = useState({});

  if (!selectedPrison || !visitingDetails) {
    return <div>Please complete the previous steps</div>;
  }

  const prisonInventory = inventory[selectedPrison.id] || [];
  const categories = ['All', ...new Set(prisonInventory.map(item => item.category))];
  const filteredItems = selectedCategory === 'All' 
    ? prisonInventory 
    : prisonInventory.filter(item => item.category === selectedCategory);

  const handleAddToCart = (item) => {
    addToCart(item, selectedPrison.id);
    setAddedItems(prev => ({
      ...prev,
      [item.id]: true
    }));
    setTimeout(() => {
      setAddedItems(prev => ({
        ...prev,
        [item.id]: false
      }));
    }, 1000);
  };

  const getItemQuantityInCart = (itemId) => {
    return cart
      .filter(cartItem => cartItem.id === itemId)
      .reduce((sum, item) => sum + item.quantity, 0);
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="bg-white dark:bg-government-800 rounded-xl shadow-lg p-6 mb-6 transition-colors duration-300">
          <h1 className="text-3xl font-bold text-government-900 dark:text-white mb-2">
            {selectedPrison.name} Menu
          </h1>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-government-600 dark:text-government-300">
            <div>
              <span className="font-medium">Visitor:</span> {visitingDetails.visitorName}
            </div>
            <div>
              <span className="font-medium">Prisoner:</span> {visitingDetails.prisonerName}
            </div>
            <div>
              <span className="font-medium">Date:</span> {visitingDetails.visitDate}
            </div>
            <div>
              <span className="font-medium">Time:</span> {visitingDetails.visitTime}
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-primary-500 text-white'
                  : 'bg-white dark:bg-government-800 text-government-600 dark:text-government-300 hover:bg-government-50 dark:hover:bg-government-700'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Menu Items */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredItems.map((item, index) => {
          const quantityInCart = getItemQuantityInCart(item.id);
          const isAdded = addedItems[item.id];

          return (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-government-800 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
            >
              <div className="aspect-w-16 aspect-h-9">
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-full h-48 object-cover"
                />
              </div>
              <div className="p-6">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-semibold text-government-900 dark:text-white">
                    {item.name}
                  </h3>
                  <span className="text-lg font-bold text-primary-500">
                    £{item.price.toFixed(2)}
                  </span>
                </div>
                <p className="text-sm text-government-600 dark:text-government-300 mb-3">
                  {item.category}
                </p>
                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <span className={`font-medium ${
                      item.stock > 0 
                        ? 'text-green-600 dark:text-green-400' 
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {item.stock > 0 ? `${item.stock} in stock` : 'Out of stock'}
                    </span>
                    {quantityInCart > 0 && (
                      <div className="text-primary-500 font-medium">
                        {quantityInCart} in cart
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleAddToCart(item)}
                    disabled={item.stock === 0}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2 ${
                      item.stock === 0
                        ? 'bg-government-200 dark:bg-government-600 text-government-500 dark:text-government-400 cursor-not-allowed'
                        : isAdded
                        ? 'bg-green-600 text-white'
                        : 'bg-primary-500 hover:bg-primary-600 text-white'
                    }`}
                  >
                    <SafeIcon icon={isAdded ? FiMinus : FiPlus} className="w-4 h-4" />
                    <span>{isAdded ? 'Added!' : 'Add'}</span>
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {filteredItems.length === 0 && (
        <div className="text-center py-12">
          <p className="text-government-600 dark:text-government-300 text-lg">
            No items available in this category
          </p>
        </div>
      )}

      {/* Cart Summary */}
      {cart.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-6 right-6 bg-primary-500 text-white p-4 rounded-xl shadow-xl"
        >
          <div className="flex items-center space-x-3">
            <SafeIcon icon={FiShoppingCart} className="w-6 h-6" />
            <div>
              <div className="font-semibold">
                {cart.length} item{cart.length !== 1 ? 's' : ''} in cart
              </div>
              <div className="text-sm opacity-90">
                £{cart.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(2)}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default Menu;